// ATAD-136 — client-side checks for ZIP code file uploads.
//
// The server (server/services/zipCodes.js) is authoritative: it reads the
// whole file, normalizes every row and reports how many ZIPs were loaded and
// how many rows were skipped. This only previews the first rows so an obvious
// mistake is caught before uploading. Keep normalizeZip and the aliases in
// sync with the server module — both test files share the same case table.

export type FileKind = 'id' | 'zip';

/** Header names that mark the ZIP column, compared after stripping spaces/_/-. */
export const ZIP_HEADER_ALIASES = ['zip', 'zipcode', 'zipcodes', 'zip5', 'postalcode'];

export const MAX_ZIP_FILE_BYTES = 10 * 1024 * 1024;
export const MAX_ZIPS_PER_FILE = 10000;

/** Control characters no text CSV contains — a renamed binary file. */
const BINARY_CHARS = /[\u0000-\u0008\u000e-\u001f]/;

function cleanCell(raw: unknown): string {
  if (raw == null) return '';
  let s = String(raw).replace(/^\uFEFF/, '').trim();
  while (
    s.length >= 2 &&
    ((s.startsWith('"') && s.endsWith('"')) ||
      (s.startsWith("'") && s.endsWith("'")))
  ) {
    s = s.slice(1, -1).trim();
  }
  return s;
}

export function isZipHeader(cell: unknown): boolean {
  const compact = cleanCell(cell).toLowerCase().replace(/[\s_-]+/g, '');
  return ZIP_HEADER_ALIASES.includes(compact);
}

/** Normalize one cell to a 5-digit ZIP (restoring Excel-stripped zeros), or null. */
export function normalizeZip(raw: unknown): string | null {
  const s = cleanCell(raw);
  let zip: string | null = null;

  if (/^\d{5}$/.test(s)) zip = s;
  else if (/^\d{5}[-\s]?\d{4}$/.test(s)) zip = s.slice(0, 5);
  else if (/^\d{3,4}$/.test(s)) zip = s.padStart(5, '0');
  else if (/^\d{8}$/.test(s)) zip = s.padStart(9, '0').slice(0, 5);

  if (!zip || zip === '00000') return null;
  return zip;
}

/**
 * Check the first rows of a ZIP file parsed with `header: false`.
 *
 * @returns the problem to show (without FILE_FORMAT_HINT), or null when the
 *   preview contains at least one usable ZIP code
 */
export function validateZipPreview(rows: unknown[][] | undefined): string | null {
  const nonBlank = (rows ?? []).filter(
    (row) => Array.isArray(row) && row.some((cell) => cleanCell(cell) !== '')
  );

  if (nonBlank.length === 0) {
    return 'Your file appears to be empty.';
  }

  if (nonBlank.some((row) => row.some((cell) => BINARY_CHARS.test(String(cell ?? ''))))) {
    return "This doesn't look like a CSV file. If you exported it from Excel, re-save it as CSV (comma-separated) rather than renaming the file.";
  }

  const [first, ...rest] = nonBlank;
  const aliasIndex = first.findIndex(isZipHeader);
  const column = aliasIndex === -1 ? 0 : aliasIndex;
  const dataRows = aliasIndex === -1 ? nonBlank : rest;

  if (!dataRows.some((row) => normalizeZip(row[column]) !== null)) {
    return "We couldn't find any 5-digit ZIP codes at the start of your file. Put one ZIP code per row (a 'zip' header is optional).";
  }

  return null;
}
